import { FileText, Map, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import Slide from '@/components/shared/Slide';
import { Separator } from '@/components/ui/separator';
import type { ViewMode } from '@/types/roadmap.types';

interface Props {
  mode: ViewMode;
}

const deliverables = [
  {
    icon: FileText,
    title: 'Funding Roadmap',
    description:
      'Detailed report with programs, eligibility, award ranges, cost categories, compliance calendar, and go/no-go recommendations.',
  },
  {
    icon: Map,
    title: 'Demand Map',
    description:
      'Buyer-side funded demand signals aligned with your technology and market position across target geographies.',
  },
  {
    icon: MessageSquare,
    title: 'Review Session',
    description:
      '60-minute dedicated walkthrough with recommendations, priority ranking, and Q&A.',
  },
];

const steps = [
  'Confirm engagement',
  'Complete intake questionnaire',
  'Receive your roadmap',
];

function StepTimeline({ animate }: { animate: boolean }) {
  return (
    <div className="relative">
      {/* Continuous connector line spanning all 3 columns */}
      <div className="absolute top-4 left-[16.667%] right-[16.667%] h-0.5 bg-border/40" />
      <motion.div
        className="absolute top-4 left-[16.667%] right-[16.667%] h-0.5 bg-accent/30 origin-left"
        initial={animate ? { scaleX: 0 } : false}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.2, delay: animate ? 0.3 : 0, ease: 'easeOut' }}
      />

      <div className="grid grid-cols-3 gap-5 relative">
        {steps.map((step, i) => (
          <motion.div
            key={step}
            className="flex flex-col items-center text-center"
            initial={animate ? { opacity: 0, y: 12 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: animate ? 0.3 + i * 0.3 : 0 }}
          >
            <motion.div
              className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold mb-3 ring-4 ring-background"
              initial={animate ? { scale: 0 } : false}
              animate={{ scale: 1 }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 20,
                delay: animate ? 0.35 + i * 0.3 : 0,
              }}
            >
              {i + 1}
            </motion.div>
            <span className="text-sm font-medium text-foreground">{step}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function DeliverablesSlide({ mode }: Props) {
  const animate = mode === 'presentation';

  return (
    <Slide mode={mode}>
      <div className="w-full max-w-4xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Deliverables
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-10">
          What You Receive
        </h2>

        <div className="grid grid-cols-3 gap-5 mb-12">
          {deliverables.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="rounded-xl border border-border/60 p-5 flex flex-col"
            >
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-accent" />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-2">
                {title}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed flex-1">
                {description}
              </p>
            </div>
          ))}
        </div>

        <Separator className="mb-8" />

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-6">
          Next Steps
        </p>

        <StepTimeline animate={animate} />
      </div>
    </Slide>
  );
}
